'use strict';
(() => {
  const el = id => document.getElementById(id);
  const config = window.FAMILY_CIRCLE_AUTH || {};
  let registrationPromise = null;

  function session() { return window.FAMILY_CIRCLE_SESSION; }
  function userId() { return session()?.getUser()?.id || null; }
  function setStatus(message) {
    const status = el('pushStatus');
    if (status) status.textContent = message;
  }
  function setEnabled(enabled) {
    const enable = el('enablePush'), disable = el('disablePush');
    if (enable) enable.classList.toggle('hidden', enabled);
    if (disable) disable.classList.toggle('hidden', !enabled);
  }
  function applicationServerKey(value) {
    const normalized = String(value || '').replace(/-/g, '+').replace(/_/g, '/');
    const binary = atob(normalized + '='.repeat((4 - normalized.length % 4) % 4));
    return Uint8Array.from(binary, character => character.charCodeAt(0));
  }
  function supported() {
    return window.isSecureContext && 'serviceWorker' in navigator &&
      'PushManager' in window && 'Notification' in window;
  }
  function getRegistration() {
    if (!supported()) return Promise.reject(new Error('unsupported'));
    if (!registrationPromise) {
      const workerUrl = config.pushWorkerUrl || '/sw.js';
      registrationPromise = navigator.serviceWorker.register(workerUrl).then(async registration => {
        if (registration.active) return registration;
        const worker = registration.installing || registration.waiting;
        if (!worker) throw new Error('worker_unavailable');
        await new Promise((resolve, reject) => {
          const timeout = setTimeout(() => reject(new Error('worker_timeout')), 15000);
          function changed() {
            if (worker.state === 'activated') { clearTimeout(timeout); worker.removeEventListener('statechange', changed); resolve(); }
            if (worker.state === 'redundant') { clearTimeout(timeout); worker.removeEventListener('statechange', changed); reject(new Error('worker_failed')); }
          }
          worker.addEventListener('statechange', changed);
          changed();
        });
        return registration;
      }).catch(error => { registrationPromise = null; throw error; });
    }
    return registrationPromise;
  }
  async function invoke(functionName, body) {
    const currentSession = session();
    if (!userId() || !currentSession?.client?.functions?.invoke) throw new Error('sign_in_required');
    const { data, error } = await currentSession.client.functions.invoke(functionName, { body });
    if (error) throw error;
    return data || {};
  }
  async function registerSubscription(subscription) {
    return invoke('family-circle-push', {
      action: 'subscribe', subscription: subscription.toJSON()
    });
  }
  async function refreshStatus() {
    if (!userId()) {
      setEnabled(false); setStatus('Sign in to enable alerts on this device.'); return;
    }
    if (!supported()) {
      setEnabled(false); setStatus('This browser or device does not support web notifications.'); return;
    }
    if (!config.pushVapidPublicKey) {
      setEnabled(false); setStatus('Phone alerts are not configured yet. Please try again later.'); return;
    }
    try {
      const permission = Notification.permission;
      if (permission === 'denied') {
        setEnabled(false); setStatus('Notifications are blocked in this browser. Allow them in site settings, then try again.'); return;
      }
      const sw = await getRegistration();
      const subscription = await sw.pushManager.getSubscription();
      if (permission === 'granted' && subscription) {
        await registerSubscription(subscription);
        setEnabled(true); setStatus('Alerts are enabled on this device.'); return;
      }
      setEnabled(false); setStatus('Alerts are off on this device. Turn them on to receive circle updates.');
    } catch {
      setEnabled(false); setStatus('We couldn’t check notification settings. Please try again.');
    }
  }
  async function enableThisDevice() {
    if (!userId()) throw new Error('sign_in_required');
    if (!config.pushVapidPublicKey) throw new Error('not_configured');
    if (!supported()) throw new Error('unsupported');
    let permission = Notification.permission;
    if (permission === 'default') permission = await Notification.requestPermission();
    if (permission !== 'granted') throw new Error(permission === 'denied' ? 'permission_denied' : 'permission_not_granted');
    const sw = await getRegistration();
    const subscription = await sw.pushManager.getSubscription() || await sw.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: applicationServerKey(config.pushVapidPublicKey)
    });
    await registerSubscription(subscription);
    setEnabled(true); setStatus('Alerts are enabled on this device.');
  }
  async function removeThisDevice() {
    if (!supported()) return;
    try {
      const sw = await getRegistration();
      const subscription = await sw.pushManager.getSubscription();
      if (!subscription) { setEnabled(false); return; }
      if (userId()) await invoke('family-circle-push', {
        action: 'unsubscribe', endpoint: subscription.endpoint
      });
      await subscription.unsubscribe();
      setEnabled(false); setStatus('Alerts are off on this device.');
    } catch {
      throw new Error('Could not turn off alerts on this device. Please try again.');
    }
  }
  async function notify(message) {
    if (!userId() || !message?.id) return { ok: false, accepted: 0 };
    const result = await invoke('family-circle-push', { action: 'notify', messageId: message.id });
    return { ok: true, accepted: Number(result.accepted) || 0 };
  }

  window.FAMILY_CIRCLE_PUSH = Object.freeze({ notify, removeThisDevice });
  el('enablePush')?.addEventListener('click', async () => {
    const button = el('enablePush'); button.disabled = true; setStatus('Enabling alerts…');
    try { await enableThisDevice(); }
    catch (error) {
      const messages = {
        not_configured: 'Phone alerts are not configured yet. Please try again later.',
        unsupported: 'This browser or device does not support web notifications.',
        permission_denied: 'Notifications are blocked in this browser. Allow them in site settings, then try again.',
        permission_not_granted: 'Notification permission was not granted.',
        sign_in_required: 'Sign in before enabling alerts.'
      };
      setStatus(messages[error?.message] || 'We couldn’t enable alerts. Please try again.');
    } finally { button.disabled = false; }
  });
  el('disablePush')?.addEventListener('click', async () => {
    const button = el('disablePush'); button.disabled = true; setStatus('Turning alerts off…');
    try { await removeThisDevice(); }
    catch (error) { setStatus(error.message); }
    finally { button.disabled = false; }
  });
  window.addEventListener('family-circle-auth', () => { void refreshStatus(); });
  if ('serviceWorker' in navigator && window.isSecureContext) {
    window.addEventListener('load', () => {
      getRegistration().then(() => { void refreshStatus(); }).catch(() => {
        registrationPromise = null;
        setStatus('This device could not prepare notification support. Refresh and try again.');
      });
    });
  }
  void refreshStatus();
})();
